import ApiClient, { DevicePubnubAuth } from '@raydiant/api-client-js';
import Pubnub from 'pubnub';
import render from './render';
import handleDeviceDeleted from './utilities/handleDeviceDeleted';
import config from './config';

export interface InitOptions {
  container: HTMLElement;
  deviceId: string;
  sessionId: string;
  accessToken: string;
  apiUrl: string;
  pubnubPublishKey: string;
  pubnubSubscribeKey: string;
  pauseWhileOffline: boolean;
  appRendererUrl?: string;
  appRendererOfflineSupport?: boolean;
  onDeviceDeleted: () => void;
  logger: Logger;
  dashUrl?: string;
  injectAds?: boolean;
  signUpPage?: string;
  signUpQrCode?: React.ReactNode;
}

let pubnubClient: Pubnub;
let pubnubAuthExpiry: number;

export default async function init({
  container,
  deviceId,
  sessionId,
  accessToken,
  apiUrl,
  pubnubPublishKey,
  pubnubSubscribeKey,
  pauseWhileOffline,
  appRendererUrl,
  appRendererOfflineSupport,
  onDeviceDeleted,
  logger,
  dashUrl,
  injectAds,
  signUpPage,
  signUpQrCode,
}: InitOptions): Promise<void> {
  const apiClient = new ApiClient({
    accessToken,
    baseUrl: apiUrl,
    isBearerToken: false,
    fetcher: window.fetch.bind(window),
  });

  const { authKey, channelId, expiresAt } = await getDevicePubnubAuth(
    apiClient,
    sessionId,
    onDeviceDeleted,
  );

  const createPubnubClient = (authKey: string) => {
    const pubnub = new Pubnub({
      uuid: sessionId,
      publishKey: pubnubPublishKey,
      subscribeKey: pubnubSubscribeKey,
      presenceTimeout: config.pubnubPresenceTimeoutSeconds,
      heartbeatInterval: config.pubnubHeartbeatIntervalSeconds,
      ssl: true,
    });
    pubnub.setToken(authKey);
    return pubnub;
  };

  pubnubClient = createPubnubClient(authKey);
  pubnubAuthExpiry = expiresAt;

  // Subscribe to device channel.

  logger.info(`Subscribing to pubnub channel '${channelId}'`);

  pubnubClient.subscribe({
    channels: [channelId],
    withPresence: true,
  });

  // Unsubscribe when the window closes. Browser support for this event could
  // be hit or miss. If unsubscribe doesn't get called then this session
  // will time out according to the pubnub presence timeout setting.
  window.addEventListener('beforeunload', () => {
    pubnubClient.unsubscribeAll();
  });

  // Render app with provided pubnub client and channel id. These parameters can
  // change when a pubnub token is refreshed.
  const renderApp = (pubnubClient: Pubnub, channelId: string) =>
    render({
      deviceId,
      channelId,
      sessionId,
      apiClient,
      pubnubClient,
      container,
      appRendererUrl,
      appRendererOfflineSupport,
      pauseWhileOffline,
      onDeviceDeleted,
      logger,
      dashUrl,
      injectAds,
      signUpPage,
      signUpQrCode,
    });

  // Check if we need to refresh the pubnub auth key every 2 minutes.
  setInterval(async () => {
    const lastExpiresAt = +new Date(pubnubAuthExpiry * 1000);
    const now = +new Date();

    // Refresh the auth key 15 minutes before it expires.
    const timeUntilExpiryMs = lastExpiresAt - now;
    const shouldRefreshAuthKey = timeUntilExpiryMs <= 15 * 60 * 1000;
    if (!shouldRefreshAuthKey) {
      logger.info(`Pubnub token expires in ${timeUntilExpiryMs}ms`);
      return;
    }

    logger.info('Refreshing pubnub auth token');

    // Fetch new authKey.
    const { authKey, channelId, expiresAt } = await getDevicePubnubAuth(
      apiClient,
      sessionId,
      onDeviceDeleted,
    );

    logger.info(`New pubnub auth token expires at '${expiresAt}'`);

    // Unsubscribe from pubnub before creating a new pubnub client.
    if (pubnubClient) {
      pubnubClient.unsubscribeAll();
    }

    pubnubClient = createPubnubClient(authKey);
    pubnubAuthExpiry = expiresAt;

    // re-subscribe to device channel.
    pubnubClient.subscribe({ channels: [channelId], withPresence: true });

    // Re-render with new pubnub client and channelId.
    await renderApp(pubnubClient, channelId);
  }, 1000 * 60 * 2);

  // Initial render.
  await renderApp(pubnubClient, channelId);
}

const getDevicePubnubAuth = async (
  apiClient: ApiClient,
  sessionId: string,
  onDeviceDeleted: () => void,
): Promise<DevicePubnubAuth> => {
  try {
    return await handleDeviceDeleted(
      apiClient.devicePubnubAuth(sessionId),
      onDeviceDeleted,
    );
  } catch (err) {
    throw new Error(
      `Failed to get pubnub auth with '${(err as Error).message}'`,
    );
  }
};
